import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { watchlistsState } from 'states';
import {
  compassPosColorState,
  compassNegColorState,
  compassActiveSymbolsState,
} from 'states/compass';
import { COMPASS_DEFAULT_ZOOM_DOMAIN } from 'config/compass';
import {
  CompassConfig,
  CompassParams,
  CompassSymbolDataKey,
  CompassZoomDomain,
  StrategyCompassMode,
  StrategyCompassModeXMap,
  StrategyCompassModeYMap,
  StrategyCompassModeZMap,
  StrategyCompassXYZAxis,
  convertAxisToDataKey,
} from 'types/compass';
import { isValidCompassSymbol } from 'util/compass';
import { selectedWatchlistsSymbols } from 'util/shared/watchlists';
import { useCompassData } from 'hooks/scanners/useCompassData';
import { CompassContext } from './useCompassCtx';

interface CompassProviderProps {
  config: CompassConfig;
  onConfigChange: (patch: Partial<CompassConfig>) => void;
  children: ReactNode;
}

export const CompassProvider = ({
  config,
  onConfigChange,
  children,
}: CompassProviderProps) => {
  const watchlists = useRecoilValue(watchlistsState);
  const posColor = useRecoilValue(compassPosColorState);
  const negColor = useRecoilValue(compassNegColorState);

  // Keep the recoil bridge atom in sync so EquityStockScanner can add symbols
  const setCompassActiveSymbolsRecoil = useSetRecoilState(
    compassActiveSymbolsState,
  );
  useEffect(() => {
    setCompassActiveSymbolsRecoil(config.symbols);
  }, [config.symbols, setCompassActiveSymbolsRecoil]);

  // --- Transient UI state ---
  const [hoveredSymbol, setHoveredSymbol] = useState<string | undefined>(
    undefined,
  );
  const [hoveredSymData, setHoveredSymData] = useState<string | undefined>(
    undefined,
  );
  const [hiddenSymbols, setHiddenSymbols] = useState<Set<string>>(new Set());
  const [zoomDomain, setZoomDomain] = useState<CompassZoomDomain>(
    COMPASS_DEFAULT_ZOOM_DOMAIN,
  );
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomStartPoint, setZoomStartPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [zoomEndPoint, setZoomEndPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  // --- Compute validated axes from config (same logic as useCompassParams) ---
  const xAxis = useMemo(() => {
    if (config.mode === StrategyCompassMode.Compass) {
      return StrategyCompassXYZAxis.SkewRank;
    }
    const acceptableX = StrategyCompassModeXMap.get(config.mode)!;
    if (config.xAxis != null && acceptableX.includes(config.xAxis)) {
      return config.xAxis;
    }
    return acceptableX[0];
  }, [config.mode, config.xAxis]);

  const yAxis = useMemo(() => {
    if (config.mode === StrategyCompassMode.Compass) {
      return StrategyCompassXYZAxis.IvRank;
    }
    const acceptableY = StrategyCompassModeYMap.get(config.mode)!;
    if (config.yAxis != null && acceptableY.includes(config.yAxis)) {
      return config.yAxis;
    }
    return acceptableY[0];
  }, [config.mode, config.yAxis]);

  const zAxis = useMemo(() => {
    if (config.mode === StrategyCompassMode.Compass) {
      return undefined;
    }
    const acceptableZ = StrategyCompassModeZMap.get(config.mode);
    if (acceptableZ == null) {
      return undefined;
    }
    if (config.zAxis != null && acceptableZ.includes(config.zAxis)) {
      return config.zAxis;
    }
    return undefined;
  }, [config.mode, config.zAxis]);

  const xAxisDataKey = useMemo(() => convertAxisToDataKey(xAxis), [xAxis]);
  const yAxisDataKey = useMemo(() => convertAxisToDataKey(yAxis), [yAxis]);
  const zAxisDataKey: CompassSymbolDataKey | undefined = useMemo(
    () =>
      zAxis == null || zAxis === StrategyCompassXYZAxis.None
        ? undefined
        : convertAxisToDataKey(zAxis),
    [zAxis],
  );

  // --- Axis setters with validation ---
  const setXAxis = useCallback(
    (newX: StrategyCompassXYZAxis) => {
      const acceptableX = StrategyCompassModeXMap.get(config.mode)!;
      if (acceptableX.includes(newX)) {
        onConfigChange({ xAxis: newX });
      }
    },
    [config.mode, onConfigChange],
  );

  const setYAxis = useCallback(
    (newY: StrategyCompassXYZAxis) => {
      const acceptableY = StrategyCompassModeYMap.get(config.mode)!;
      if (acceptableY.includes(newY)) {
        onConfigChange({ yAxis: newY });
      }
    },
    [config.mode, onConfigChange],
  );

  const setZAxis = useCallback(
    (newZ: StrategyCompassXYZAxis) => {
      const acceptableZ = StrategyCompassModeZMap.get(config.mode);
      if (acceptableZ != null && acceptableZ.includes(newZ)) {
        onConfigChange({ zAxis: newZ });
      }
    },
    [config.mode, onConfigChange],
  );

  const setMode = useCallback(
    (newMode: StrategyCompassMode) => {
      const acceptableX = StrategyCompassModeXMap.get(newMode)!;
      const acceptableY = StrategyCompassModeYMap.get(newMode)!;
      const acceptableZ = StrategyCompassModeZMap.get(newMode);

      onConfigChange({
        mode: newMode,
        xAxis: acceptableX[0],
        yAxis: acceptableY[0],
        zAxis:
          newMode === StrategyCompassMode.Compass
            ? undefined
            : acceptableZ?.[0],
      });
    },
    [onConfigChange],
  );

  const setSyms = useCallback(
    (newSyms: string[]) => {
      onConfigChange({
        symbols: [...new Set(newSyms.filter(isValidCompassSymbol))],
      });
    },
    [onConfigChange],
  );

  const setActiveWatchlistIds = useCallback(
    (ids: number[]) => {
      const idsRemoved = config.activeWatchlistIds.filter(
        (id) => !ids.includes(id),
      );
      const idsAdded = ids.filter(
        (id) => !config.activeWatchlistIds.includes(id),
      );

      const watchlistSymsAdded = selectedWatchlistsSymbols(
        watchlists,
        idsAdded,
      );
      const watchlistSymsRemoved = selectedWatchlistsSymbols(
        watchlists,
        idsRemoved,
      );

      const newSyms = [
        ...new Set(
          config.symbols
            .concat(watchlistSymsAdded)
            .filter((s: string) => !watchlistSymsRemoved.includes(s))
            .filter(isValidCompassSymbol),
        ),
      ];

      onConfigChange({
        symbols: newSyms,
        activeWatchlistIds: ids,
      });
    },
    [config.activeWatchlistIds, config.symbols, watchlists, onConfigChange],
  );

  const setActiveWatchlistIdsState = useCallback(
    (ids: number[]) => {
      onConfigChange({ activeWatchlistIds: ids });
    },
    [onConfigChange],
  );

  // --- Compose CompassParams ---
  const compassParams: CompassParams = useMemo(
    () => ({
      xAxis,
      yAxis,
      zAxis,
      xAxisDataKey,
      yAxisDataKey,
      zAxisDataKey,
      mode: config.mode,
      setXAxis,
      setYAxis,
      setZAxis,
      setMode,
      syms: config.symbols,
      setSyms,
      editable: true,
      activeWatchlistIds: config.activeWatchlistIds,
      setActiveWatchlistIds,
      setActiveWatchlistIdsState,
    }),
    [
      xAxis,
      yAxis,
      zAxis,
      xAxisDataKey,
      yAxisDataKey,
      zAxisDataKey,
      config.mode,
      config.symbols,
      config.activeWatchlistIds,
      setXAxis,
      setYAxis,
      setZAxis,
      setMode,
      setSyms,
      setActiveWatchlistIds,
      setActiveWatchlistIdsState,
    ],
  );

  // --- Data fetching ---
  const compassData = useCompassData({
    compassParams,
    hiddenSymbols,
    historyLookback: config.historyLookback,
  });

  return (
    <CompassContext.Provider
      value={{
        config,
        updateConfig: onConfigChange,
        compassParams,
        compassData,
        hoveredSymbol,
        setHoveredSymbol,
        hoveredSymData,
        setHoveredSymData,
        hiddenSymbols,
        setHiddenSymbols,
        zoomDomain,
        setZoomDomain,
        isZoomed,
        setIsZoomed,
        zoomStartPoint,
        setZoomStartPoint,
        zoomEndPoint,
        setZoomEndPoint,
        isSelecting,
        setIsSelecting,
        posColor,
        negColor,
      }}
    >
      {children}
    </CompassContext.Provider>
  );
};
